/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "react-oidc-context";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import config from "./config.json";

interface GlueupEvent {
    event_id: string;
    title: string | null;
    start_ts: string | null;
    end_ts: string | null;
    venue: string | null;
    summary: string | null;
    description: string | null;
    tags: string[];
    speakers: string[];
    organizers: string[];
    languages: string[];
    source_url: string | null;
    registration_url: string | null;
}

// Column list in the order it will appear in the CSV.
const COLUMNS: { key: keyof GlueupEvent; label: string }[] = [
    { key: "event_id",         label: "event_id" },
    { key: "title",            label: "title" },
    { key: "start_ts",         label: "start_ts" },
    { key: "end_ts",           label: "end_ts" },
    { key: "venue",            label: "venue" },
    { key: "summary",          label: "summary" },
    { key: "description",      label: "description" },
    { key: "tags",             label: "tags" },
    { key: "speakers",         label: "speakers" },
    { key: "organizers",       label: "organizers" },
    { key: "languages",        label: "languages" },
    { key: "source_url",       label: "source_url" },
    { key: "registration_url", label: "registration_url" },
];

const LIST_SEPARATOR = "; ";

// Excel opens UTF-8 CSVs correctly when a BOM is present.
const UTF8_BOM = "﻿";

function csvEscape(value: unknown): string {
    if (value === null || value === undefined) return "";
    const str = String(value);
    // Always quote so semicolons / newlines / quotes inside fields don't break rows.
    return `"${str.replace(/"/g, '""')}"`;
}

function formatCell(value: unknown): string {
    if (Array.isArray(value)) return value.join(LIST_SEPARATOR);
    if (value === null || value === undefined) return "";
    return String(value);
}

function downloadCsv(filename: string, csv: string) {
    const blob = new Blob([UTF8_BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

interface Props {
    open: boolean;
    onClose: () => void;
}

const GlueupEventsExport: React.FC<Props> = ({ open, onClose }) => {
    const auth = useAuth();
    const [events, setEvents] = useState<GlueupEvent[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [mode, setMode] = useState<"events" | "event_tags">("events");
    const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
        new Set(COLUMNS.map((c) => c.key as string))
    );

    useEffect(() => {
        if (!open) return;
        if (events !== null) return; // already loaded this session
        const fetchEvents = async () => {
            const userApiKey = auth.user?.profile?.["custom:api_key"] as string | undefined;
            if (!auth.user?.id_token || !userApiKey) {
                setError("Authentication error: API key or token is missing.");
                return;
            }
            const base = config.api.chatbot.url.endsWith("/")
                ? config.api.chatbot.url.slice(0, -1)
                : config.api.chatbot.url;
            try {
                setIsLoading(true);
                setError(null);
                const resp = await axios.get(`${base}/glueup_events`, {
                    headers: {
                        Authorization: auth.user.id_token,
                        "X-Api-Key": userApiKey,
                    },
                    timeout: config.timeouts.chatApi,
                });
                const rows = (resp.data?.events || []) as GlueupEvent[];
                setEvents(rows);
            } catch (err) {
                console.error("Failed to fetch glueup events:", err);
                setError("Failed to load events. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchEvents();
    }, [open, events, auth]);

    const toggleColumn = (key: string) => {
        setSelectedColumns((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleAll = (checked: boolean) => {
        setSelectedColumns(checked ? new Set(COLUMNS.map((c) => c.key as string)) : new Set());
    };

    const eventTagRowCount = useMemo(() => {
        if (!events) return 0;
        return events.reduce((n, e) => n + Math.max(e.tags?.length || 0, 0), 0);
    }, [events]);

    const handleDownload = () => {
        if (!events) return;
        if (mode === "events") {
            const cols = COLUMNS.filter((c) => selectedColumns.has(c.key as string));
            if (cols.length === 0) return;
            const header = cols.map((c) => csvEscape(c.label)).join(";");
            const rows = events.map((e) =>
                cols.map((c) => csvEscape(formatCell(e[c.key]))).join(";")
            );
            downloadCsv("climate_week_zurich_events.csv", [header, ...rows].join("\n"));
        } else {
            // Satellite event–tag table: one row per (event, tag) pair.
            // Events with no tags still get a single row (tag = "") so they
            // aren't silently dropped from the satellite — important for
            // Tableau joins that expect every event to appear.
            const header = ["event_id", "title", "start_ts", "venue", "tag"]
                .map(csvEscape)
                .join(";");
            const rows: string[] = [];
            events.forEach((e) => {
                const tags = e.tags && e.tags.length > 0 ? e.tags : [""];
                tags.forEach((tag) => {
                    rows.push(
                        [e.event_id, e.title, e.start_ts, e.venue, tag]
                            .map((v) => csvEscape(formatCell(v)))
                            .join(";")
                    );
                });
            });
            downloadCsv("climate_week_zurich_event_tags.csv", [header, ...rows].join("\n"));
        }
    };

    const allSelected = selectedColumns.size === COLUMNS.length;
    const someSelected = selectedColumns.size > 0 && !allSelected;

    const downloadDisabled =
        !events ||
        isLoading ||
        (mode === "events" && selectedColumns.size === 0) ||
        (mode === "event_tags" && eventTagRowCount === 0);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, position: "relative" }}>
                Export Climate Week Zurich events
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: "absolute", right: 8, top: 8, color: (t) => t.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <DialogContentText sx={{ mb: 2 }}>
                    Pick a table shape and the columns to include. The file downloads as UTF-8 CSV.
                </DialogContentText>

                {isLoading && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                            Loading events…
                        </Typography>
                    </Stack>
                )}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">Table shape</FormLabel>
                    <RadioGroup
                        row
                        value={mode}
                        onChange={(e) => setMode(e.target.value as "events" | "event_tags")}
                    >
                        <FormControlLabel
                            value="events"
                            control={<Radio />}
                            label="One row per event"
                        />
                        <FormControlLabel
                            value="event_tags"
                            control={<Radio />}
                            label="One row per event–tag"
                        />
                    </RadioGroup>
                </FormControl>

                <Divider sx={{ mb: 2 }} />

                {mode === "events" ? (
                    <>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">Columns</Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onChange={(e) => toggleAll(e.target.checked)}
                                    />
                                }
                                label="Select all"
                            />
                        </Stack>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 0.25,
                                mb: 1,
                            }}
                        >
                            {COLUMNS.map((c) => (
                                <FormControlLabel
                                    key={c.key as string}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={selectedColumns.has(c.key as string)}
                                            onChange={() => toggleColumn(c.key as string)}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                            {c.label}
                                        </Typography>
                                    }
                                />
                            ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            List-typed columns (tags, speakers, organizers, languages) are joined with
                            "{LIST_SEPARATOR}".
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Satellite table
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            One row per event–tag combination. Fixed columns:
                            {" "}
                            <Box component="code" sx={{ fontFamily: "monospace" }}>
                                event_id, title, start_ts, venue, tag.
                            </Box>
                        </Typography>
                    </>
                )}

                {events && (
                    <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.disabled" }}>
                        {mode === "events"
                            ? `${events.length} event${events.length === 1 ? "" : "s"} will be exported.`
                            : `${events.length} events → ${eventTagRowCount} event–tag row${eventTagRowCount === 1 ? "" : "s"}.`}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleDownload} disabled={downloadDisabled}>
                    Download CSV
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GlueupEventsExport;
